import type { StaffEmployee } from "@/hooks/use-staff";

export type IdCardEmployee = StaffEmployee;
export type IdCardSide = "front" | "back";
export type IdCardDraft = { idCardFrontId?: string; idCardBackId?: string };
export type IdCardPhoto = {id:string;side:IdCardSide;uploadedAt:string;uploadedByName:string;replacedAt:string|null};
export type IdCardPhotoList = {photos:IdCardPhoto[];legacy:boolean};

export function hasStaffIdCardPhoto(employee: Pick<IdCardEmployee,"idPhotoKey" | "idCardFrontId" | "idCardBackId" | "hasIdCardPhoto">) {
  // A legacy one-sided image remains complete. For restricted viewers the server
  // supplies the boolean without exposing either private storage identifier.
  return !!employee.idPhotoKey || !!(employee.idCardFrontId && employee.idCardBackId) || employee.hasIdCardPhoto === true;
}
export function staffMissingDetails(employee: Pick<IdCardEmployee,"phone" | "nationalId" | "idPhotoKey" | "idCardFrontId" | "idCardBackId" | "hasIdCardPhoto">) {
  return !employee.phone?.trim() || !employee.nationalId?.trim() || !hasStaffIdCardPhoto(employee);
}
export function staffMissingDetailsLabel(employee: IdCardEmployee, rtl:boolean) {
  const missing = [
    !employee.phone?.trim() ? (rtl ? "المحمول" : "mobile") : "",
    !employee.nationalId?.trim() ? (rtl ? "الرقم القومي" : "national ID") : "",
    !hasStaffIdCardPhoto(employee) ? (rtl ? "صورة البطاقة" : "ID card photo") : "",
  ].filter(Boolean);
  if (!missing.length) return "";
  return rtl ? `ينقصه ${missing.join(" و")}` : `missing ${missing.join(", ")}`;
}
export function idCardDraftBody(draft:IdCardDraft):IdCardDraft {
  return {...(draft.idCardFrontId?{idCardFrontId:draft.idCardFrontId}:{}),...(draft.idCardBackId?{idCardBackId:draft.idCardBackId}:{})};
}
export function idCardImageSize(width:number,height:number) {
  const scale=Math.min(1,1600/Math.max(width,height));
  return {width:Math.max(1,Math.round(width*scale)),height:Math.max(1,Math.round(height*scale))};
}
export async function compressStaffIdCard(file:File):Promise<Blob> {
  if(!["image/jpeg","image/png","image/webp"].includes(file.type))throw new Error("Use a JPEG, PNG or WebP image.");
  const src=URL.createObjectURL(file);
  try {
    const image=new Image();
    await new Promise<void>((resolve,reject)=>{image.onload=()=>resolve();image.onerror=()=>reject(new Error("Could not read photo. Try another photo."));image.src=src});
    const canvas=document.createElement("canvas"),size=idCardImageSize(image.naturalWidth,image.naturalHeight);
    canvas.width=size.width;canvas.height=size.height;
    const context=canvas.getContext("2d");
    if(!context)throw new Error("Could not prepare photo. Please retry.");
    context.fillStyle="#fff";context.fillRect(0,0,size.width,size.height);
    context.drawImage(image,0,0,size.width,size.height);
    return await new Promise<Blob>((resolve,reject)=>canvas.toBlob(blob=>blob?resolve(blob):reject(new Error("Could not prepare photo. Please retry.")),"image/jpeg",.86));
  } finally {URL.revokeObjectURL(src)}
}
// The JSON staffCall client cannot send raw bodies. Match the existing private
// photo transport: cookie authentication plus explicit workspace header.
export async function uploadStaffIdCard(file:File,side:IdCardSide,workspaceId:string,signal?:AbortSignal) {
  const image=await compressStaffIdCard(file);
  const response=await fetch(`/api/staff/id-card-photos?side=${side}`,{method:"POST",credentials:"include",headers:{"x-workspace-id":workspaceId,"Content-Type":image.type},body:image,signal});
  const result=await response.json();
  if(!response.ok)throw new Error(result.message||"Could not upload photo. Please retry.");
  if(typeof result.id!=="string"||!result.id)throw new Error("Could not confirm photo upload. Please retry.");
  return result as {id:string};
}
const idCardStrings:Record<string,string>={
  "Add ID photos":"إضافة صور البطاقة","Complete ID photos":"استكمال صور البطاقة",
  "Save":"حفظ","Cancel":"إلغاء","Saving…":"جارٍ الحفظ…",
  "Unable to complete employee request":"تعذّر إتمام طلب الموظف",
  "Employee not found":"الموظف غير موجود","Branch access denied":"لا تملك صلاحية الوصول إلى هذا الفرع",
  "Employee permission required":"يلزم توفر صلاحية إدارة الموظفين",
  "National ID card photo · required":"صورة بطاقة الرقم القومي · مطلوبة",
  "National ID card photo":"صورة بطاقة الرقم القومي",
  "Front":"الوجه","Back":"الظهر","Take a photo":"التقط صورة",
  "Added · tap to retake":"أُضيفت · اضغط لإعادة التصوير",
  "On file · tap to replace":"محفوظة · اضغط للاستبدال",
  "Finance compares the card with the name and number above. It's kept on his file.":"تقارن المالية البطاقة بالاسم والرقم أعلاه، وتُحفظ في ملفه.",
  "Needed: front and back of the card":"مطلوب: وجه البطاقة وظهرها",
  "ID card photos come after":"صور البطاقات تأتي لاحقًا",
  "A sheet can't carry photos. Everyone added from it shows on Employees as missing an ID card photo, and Complete takes them one after another so the manager photographs each card.":"الشيت لا يحمل صورًا. كل من يُضاف منه يظهر في الموظفين ناقصًا صورة البطاقة، و«إكمال» يعرضهم واحدًا تلو الآخر ليصوّر المدير كل بطاقة.",
  "ID card photo added":"أُضيفت صورة البطاقة","ID card photo replaced":"استُبدلت صورة البطاقة",
  "ID card photo (older, one side)":"صورة البطاقة (قديمة، وجه واحد)",
  "Add front and back":"إضافة الوجه والظهر","Retry":"إعادة المحاولة",
  "Preparing and uploading photo…":"جارٍ تجهيز الصورة ورفعها…",
  "Could not upload photo. Please retry.":"تعذّر رفع الصورة. أعد المحاولة.",
  "Could not load photo":"تعذّر تحميل الصورة",
  "Use a JPEG, PNG or WebP image.":"استخدم صورة JPEG أو PNG أو WebP.",
  "Could not read photo. Try another photo.":"تعذّرت قراءة الصورة. جرّب صورة أخرى.",
  "Could not prepare photo. Please retry.":"تعذّر تجهيز الصورة. أعد المحاولة.",
  "Could not confirm photo upload. Please retry.":"تعذّر تأكيد رفع الصورة. أعد المحاولة.",
  "Camera":"الكاميرا","Gallery":"معرض الصور",
  "Take a photo or pick one":"التقط صورة أو اختر واحدة",
  "Added · replace below":"أُضيفت · استبدلها أدناه",
  "On file · replace below":"محفوظة في الملف · استبدلها أدناه",
  "Next":"التالي","Waiting":"في الانتظار",
  "Tap to retake":"اضغط لإعادة التصوير",
  "On file · tap to retake":"محفوظة في الملف · اضغط لإعادة التصوير",
  "Take a photo of the front":"صوّر وجه البطاقة",
  "The side with his photo and name":"الجهة التي بها صورته واسمه",
  "Now take the back":"صوّر ظهر البطاقة الآن",
  "The side with the address and factory number":"الجهة التي بها العنوان ورقم المصنع",
  "Or choose the front from the gallery":"أو اختر الوجه من معرض الصور",
  "Or choose the back from the gallery":"أو اختر الظهر من معرض الصور",
  "Both sides added":"تمت إضافة الوجهين",
  "Lay the card flat in good light and fill the frame. No glare on the photo or the numbers.":"ضع البطاقة بشكل مسطّح في إضاءة جيدة واملأ بها الإطار. تجنّب انعكاس الضوء على الصورة أو الأرقام.",
};
export function idCardText(value:string,rtl:boolean) {return rtl?idCardStrings[value]||value:value}
export function idCardActivityText(action:string,rtl:boolean) {
  if(["id_card_photo_added","id-card-photo-added","ID card photo added"].includes(action))return idCardText("ID card photo added",rtl);
  if(["id_card_photo_replaced","id-card-photo-replaced","ID card photo replaced"].includes(action))return idCardText("ID card photo replaced",rtl);
  return "";
}
